import { useState } from "react";
import Image from "@Components/Image";
import StoryModal from "../StoryModal/StoryModal";
import styles from "./CreatorMarquee.module.scss";
import InlineLottie from "@Components/InlineLottie/InlineLottie";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import { pageAsset } from "@Utils/getStaticAssetPath";

// Images come from the static-assets bucket, never `import`ed — see `pageAsset`.
const asset = pageAsset("ScholarshipV2");
const lottiePlay = asset("creator-marquee-play.json");

const bandImg = asset("band.jpg");
// The gold "RECEIVED £x,000" overlays, one per prize tier. Keyed off a black ground —
// alpha from luminance, RGB untouched, so the bevel survives; see assets/.
const badge5000 = asset("badge-5000.png");
const badge4000 = asset("badge-4000.png");
const badge3000 = asset("badge-3000.png");
const badge2000 = asset("badge-2000.png");
// ⚠️  play.json IS A MODIFIED COPY of Iconly animation 3584.
//
// Every animation in the library is stroke-only — the animated set ships just
// Light and Two-Tone, with no filled style anywhere in it — so the mark arrived
// as a hollow triangle where the play.svg it replaces was solid (fill #1F1F1F),
// and the pill lost its punch against the heavy "Watch Story" label.
//
// A single `{ty:"fl"}` was inserted into the one shape layer, directly beneath
// the existing stroke and taking its colour, which brings the solid triangle back
// without touching the path or the timing. That insert is the entire diff.
// Re-exporting from the MCP returns the hollow version, so re-apply it if this
// file is ever refreshed.
// Portraits from the design — image 22317 (2122:8944) plus the two from
// 2124:8990. Squared and cropped to the card's 313px window.
const winner1 = asset("winner-1.jpg");
const winner2 = asset("winner-2.jpg");
const winner3 = asset("winner-3.jpg");
const winner4 = asset("winner-4.jpg");
const winner5 = asset("winner-5.jpg");
const gideonDanso = asset("winner-gideon-danso.jpg");
const fisayoBalogun = asset("winner-fisayo-balogun.jpg");

// Placeholder story video for every card, until each winner's own is supplied —
// swap in a real id per entry in WINNERS below and that card stops falling back.
// https://www.youtube.com/watch?v=uPivRC-AyDc — an amber video, so the stand-in is
// at least on-brand; it replaced Blender's "Big Buck Bunny", which read as random.
const PLACEHOLDER_VIDEO_ID = "uPivRC-AyDc";

interface Winner {
  /** A person's name — verbatim, never translated. */
  name: string;
  /**
   * Award tier — drives the laurel colour (gold vs silver) AND the label.
   *
   * Kept as this union rather than a translation key because it is read in a
   * `=== "Runner Up"` styling test as well as rendered; the label it maps to lives
   * in ROLE_KEYS below, so the two cannot drift.
   */
  role: "Winner" | "Runner Up";
  /** Translation key for the edition they placed in. */
  edition: string;
  image: string;
  /** YouTube id for this winner's story; falls back to the placeholder. */
  videoId?: string;
  /**
   * The gold prize overlay revealed on hover — this scholar's actual award.
   *
   * Carries its own height because the four graphics are NOT one ratio: 2.03 for the
   * £5,000 and £4,000 down to 1.80 for the £3,000 and £2,000. The slot used to be a
   * fixed 168x81, which was right for the single badge it was built around and would
   * squash the two taller ones. Width is fixed, height follows the artwork.
   */
  badge: { src: string; h: number };
}

// ── The real amberscholar roster, from production ──────────────────────────
// Replaces eight invented names (Salena Gomez, Hind Irlane, Micah Sarah, Cam Malik,
// Tyler Glass, Julia Estonia, Miriam Rao, Ashley Walseman) — none of whom existed in
// prod at all.
//
// ⚠️  ONE PORTRAIT PER PERSON, NEVER SHARED. `winner-1..5.jpg` are not stock faces, they
// are photographs of five real scholars, and they were being reused across whichever
// names needed filling — which put Stephen Sarpong Lartey's photograph on a card reading
// "Sakshi Shenoy" and Parisa Azizi's on one reading "Stephen Sarpong Lartey". Each file
// below is captioned with who it is; do not point two entries at the same one.
//
//   winner-1  Parisa Azizi        winner-4  Hekmatullah Amin
//   winner-2  Stephen S. Lartey   winner-5  Natalie Mangen
//   winner-3  Jason Zixu Zhou
//
// ⚠️  FIVE OF PROD'S TWELVE ARE MISSING HERE, deliberately: Sakshi Shenoy, Sadia Hossain
// Liya, Ann Madapatt Bennet, Karan Krishnamurthy and Khyati have no photograph in this
// repo. They are named on the live page and belong on this rail — add each one back the
// moment its headshot lands, with the video id already listed for it below.
//
// ⚠️  VIDEO IDS ARE VERIFIED AGAINST THE YOUTUBE TITLE, not copied from prod's arrays —
// prod's are wrong. Its `videos` list is shifted one edition off its names, so live right
// now Gidion Danso's card plays Khyati's interview, and every edition is mismatched the
// same way. Worth fixing there too.
//
//   Awaiting a photo:  Sakshi Shenoy XXplllz7opA · Sadia Hossain Liya qtOgXqSpCOQ
//                      Ann Madapatt Bennet 8U_IdOyubFY · Karan Krishnamurthy QFeaP6yQy1s
//                      Khyati AtvJKVALGLQ
//
// Prod grades three places an edition (Winner, 1st and 2nd Runner Up); this rail has two
// laurels, so second and third place both read as "Runner Up".
/** The label for each award tier. */
const ROLE_KEYS: Record<Winner["role"], string> = {
  Winner: "scholarship.v2.winners.role.winner",
  "Runner Up": "scholarship.v2.winners.role.runnerup",
};

const WINNERS: Winner[] = [
  {
    name: "Gideon Danso",
    role: "Winner",
    edition: "scholarship.v2.winners.edition.first",
    image: gideonDanso,
    videoId: "BTowsSvUXqw",
    badge: { src: badge5000, h: 83 },
  },
  {
    name: "Parisa Azizi Shamami",
    role: "Runner Up",
    edition: "scholarship.v2.winners.edition.first",
    image: winner1,
    videoId: "Rx6Wta61dOs",
    badge: { src: badge3000, h: 93 },
  },
  {
    name: "Hekmatullah Amin",
    role: "Runner Up",
    edition: "scholarship.v2.winners.edition.second",
    image: winner4,
    videoId: "uPivRC-AyDc",
    badge: { src: badge3000, h: 93 },
  },
  {
    name: "Fisayo Balogun",
    role: "Winner",
    edition: "scholarship.v2.winners.edition.third",
    image: fisayoBalogun,
    videoId: "yBT7RZa25sA",
    badge: { src: badge5000, h: 83 },
  },
  {
    name: "Natalie Mangen",
    role: "Runner Up",
    edition: "scholarship.v2.winners.edition.third",
    image: winner5,
    videoId: "AZLpzEl--E4",
    badge: { src: badge2000, h: 93 },
  },
  {
    name: "Jason Zixu Zhou",
    role: "Runner Up",
    edition: "scholarship.v2.winners.edition.fourth",
    image: winner3,
    videoId: "uIGljzTaFNs",
    badge: { src: badge4000, h: 84 },
  },
  {
    name: "Stephen Sarpong Lartey",
    role: "Runner Up",
    edition: "scholarship.v2.winners.edition.fourth",
    image: winner2,
    videoId: "cvWk5ENEnb8",
    badge: { src: badge2000, h: 93 },
  },
];

// Laurel pair flanking the award line. Authored here rather than exported —
// there is no laurel node in the Figma file, only a reference screenshot. Uses
// currentColor so it picks up the award green from .meta.
const Laurel = () => (
  <svg
    className={styles.laurel}
    viewBox="0 0 22 14"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
  >
    <g>
      <path d="M6.6 13.3C3.9 12.1 2.1 9.4 2.1 6.3c0-1.9.7-3.7 1.9-5.1l.85.75C3.75 3.15 3.2 4.65 3.2 6.3c0 2.6 1.5 4.9 3.85 6z" />
      <ellipse cx="2.5" cy="2.6" rx="1.35" ry="0.72" transform="rotate(-52 2.5 2.6)" />
      <ellipse cx="1.75" cy="5.1" rx="1.35" ry="0.72" transform="rotate(-22 1.75 5.1)" />
      <ellipse cx="1.95" cy="7.7" rx="1.35" ry="0.72" transform="rotate(10 1.95 7.7)" />
      <ellipse cx="3" cy="10.1" rx="1.35" ry="0.72" transform="rotate(38 3 10.1)" />
      <ellipse cx="4.85" cy="12" rx="1.3" ry="0.7" transform="rotate(62 4.85 12)" />
    </g>
    {/* Mirrored for the right-hand branch. */}
    <g transform="translate(22,0) scale(-1,1)">
      <path d="M6.6 13.3C3.9 12.1 2.1 9.4 2.1 6.3c0-1.9.7-3.7 1.9-5.1l.85.75C3.75 3.15 3.2 4.65 3.2 6.3c0 2.6 1.5 4.9 3.85 6z" />
      <ellipse cx="2.5" cy="2.6" rx="1.35" ry="0.72" transform="rotate(-52 2.5 2.6)" />
      <ellipse cx="1.75" cy="5.1" rx="1.35" ry="0.72" transform="rotate(-22 1.75 5.1)" />
      <ellipse cx="1.95" cy="7.7" rx="1.35" ry="0.72" transform="rotate(10 1.95 7.7)" />
      <ellipse cx="3" cy="10.1" rx="1.35" ry="0.72" transform="rotate(38 3 10.1)" />
      <ellipse cx="4.85" cy="12" rx="1.3" ry="0.7" transform="rotate(62 4.85 12)" />
    </g>
  </svg>
);

/**
 * Winner card — Figma node 2122:8944 (Default / On Hover).
 *
 * Anatomy: a bordered, clipped body holding a #111928 header (name + meta) with
 * the photo pulled up over its lower two thirds, and a white "Watch Story" pill
 * straddling the body's bottom edge by 18px.
 *
 * Hover adds two things over the photo — a diagonal dark gradient and the
 * "RECEIVED $10,000" graphic — both handled in CSS off the card's :hover.
 */
const Card = ({ winner, onOpen }: { winner: Winner; onOpen: () => void }) => {
  const { t } = useTranslationHook();
  // Hover is tracked on the CARD, not left to InlineLottie's own pointerenter:
  // that fires on the 18px mark alone, which would mean the play triangle only
  // answered a target a fraction the size of the button it labels. The rail
  // already pauses on card hover, so the two land together.
  const [hovered, setHovered] = useState(false);

  return (
    <li className={styles.cardItem}>
      {/* A real button, so Enter/Space and focus rings come for free. */}
      <button
        type="button"
        className={styles.card}
        onClick={onOpen}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div className={styles.body}>
          <div className={styles.header}>
            <span className={styles.name}>{winner.name}</span>
            <span className={styles.meta}>
              <span
                className={`${styles.award} ${
                  winner.role === "Runner Up" ? styles.awardRunnerUp : ""
                }`}
              >
                <Laurel />
                {t(ROLE_KEYS[winner.role])}
              </span>
              <span className={styles.bullet} aria-hidden="true" />
              <span className={styles.edition}>{t(winner.edition)}</span>
            </span>
          </div>

          <div className={styles.photo}>
            <Image
              src={winner.image}
              alt=""
              className={styles.photoImage}
              width={313}
              height={313}
              isEagerLoad
            />
            {/* Hover: diagonal scrim, then the award graphic over it.
            The reveal lives on this wrapper, not on the <Image> itself — Image
            applies `.animateOpacity.show { opacity: 1 }`, two classes, which
            would out-specify a single-class rule here and force it visible. */}
            <span className={styles.photoScrim} aria-hidden="true" />
            <span className={styles.badge} aria-hidden="true">
              <Image
                src={winner.badge.src}
                alt=""
                className={styles.badgeImage}
                width={168}
                height={winner.badge.h}
                isEagerLoad
              />
            </span>
          </div>
        </div>

        <span className={styles.watch}>
          {/* 18px slot per the design, holding the triangle at its own 12.67×11.5
            size — the SVG is preserveAspectRatio="none", so stretching it to a
            square distorts it into a blob. The rotation is on the slot because
            Image's `.animateOpacity.show { transform: none }` would cancel it. */}
          <span className={styles.playSlot} aria-hidden="true">
            <InlineLottie src={lottiePlay} size={18} autoPlay={false} play={hovered} />
          </span>
          {t("scholarship.v2.winners.watchstory")}
        </span>
      </button>
    </li>
  );
};

/**
 * A single rail of winner cards drifting left (Figma 2096:5071 — the sibling
 * "Container:transform" at the same coordinates is a duplicate showing the rail
 * mid-scroll, not a second row).
 *
 * The rail renders its cards twice and translates by exactly half the track, so
 * the second copy is in the first's place when the animation loops and the seam
 * is invisible. Pure CSS, so no scroll listener; pauses on hover.
 *
 * Behind it sits the design's hero-backdrop-band (2109:3553): a photo with two
 * white gradients over it, fading it out at the top and bottom.
 */
const CreatorMarquee = () => {
  const { t } = useTranslationHook();
  // Which winner's story is playing; null when the lightbox is closed.
  const [playing, setPlaying] = useState<Winner | null>(null);

  return (
    <section className={styles.section} aria-label={t("scholarship.v2.winners.sectionaria")}>
      <div className={styles.band} aria-hidden="true">
        <Image
          src={bandImg}
          alt=""
          className={styles.bandImage}
          width="100%"
          height="100%"
          isEagerLoad
        />
        <span className={styles.bandFade} />
      </div>

      <div className={styles.viewport}>
        <ul className={styles.track}>
          {[...WINNERS, ...WINNERS].map((winner, i) => (
            <Card
              // eslint-disable-next-line react/no-array-index-key
              key={`${winner.name}-${i}`}
              winner={winner}
              onOpen={() => setPlaying(winner)}
            />
          ))}
        </ul>
      </div>

      <StoryModal
        open={playing !== null}
        title={
          playing ? `${playing.name} — ${t(ROLE_KEYS[playing.role])}, ${t(playing.edition)}` : ""
        }
        videoId={playing?.videoId ?? PLACEHOLDER_VIDEO_ID}
        onClose={() => setPlaying(null)}
      />
    </section>
  );
};

export default wrapperHOC(CreatorMarquee, {
  componentName: "CreatorMarquee-ScholarshipV2",
  showForChina: true,
});
